import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { User } from 'firebase/auth';
import { 
  getFirestore, doc, getDoc, collection, getDocs, updateDoc,
  addDoc, deleteDoc, writeBatch
} from 'firebase/firestore';
import type { 
  UserSettings, AutofillProfile, AutofillProfileDoc, 
  LLMTextProvider, LLMEmbedProvider 
} from '@shared/types';
import { useTheme } from '../../contexts/ThemeContext';
import { saveLLMConfig as saveLLMConfigService } from '../../services/resumeMatchingFunctions';
import { createSavedResume } from '../../services/resumeService';

interface ProfileSettingsContextType {
  settings: UserSettings;
  setSettings: React.Dispatch<React.SetStateAction<UserSettings>>;
  profiles: AutofillProfileDoc[];
  setProfiles: React.Dispatch<React.SetStateAction<AutofillProfileDoc[]>>;
  selectedProfileId: string | null;
  setSelectedProfileId: React.Dispatch<React.SetStateAction<string | null>>;
  editingDetails: AutofillProfile;
  setEditingDetails: React.Dispatch<React.SetStateAction<AutofillProfile>>;
  loading: boolean;
  saving: boolean;
  uploadingFile: boolean;
  setSaving: React.Dispatch<boolean>;
  message: { type: 'success' | 'error'; text: string } | null;
  setMessage: (msg: { type: 'success' | 'error'; text: string } | null) => void;
  loadProfiles: () => Promise<AutofillProfileDoc[]>;
  saveSettings: () => Promise<void>;
  
  // LLM State
  llmTextProvider: LLMTextProvider;
  setLlmTextProvider: React.Dispatch<React.SetStateAction<LLMTextProvider>>;
  llmTextApiKey: string;
  setLlmTextApiKey: React.Dispatch<React.SetStateAction<string>>;
  llmCustomTextUrl: string;
  setLlmCustomTextUrl: React.Dispatch<React.SetStateAction<string>>;
  llmEmbedProvider: LLMEmbedProvider | '';
  setLlmEmbedProvider: React.Dispatch<React.SetStateAction<LLMEmbedProvider | ''>>;
  llmEmbedApiKey: string;
  setLlmEmbedApiKey: React.Dispatch<React.SetStateAction<string>>;
  llmCustomEmbedUrl: string;
  setLlmCustomEmbedUrl: React.Dispatch<React.SetStateAction<string>>;
  llmHasTextKey: boolean;
  llmHasEmbedKey: boolean;
  
  // Pinecone state
  pineconeApiKey: string;
  setPineconeApiKey: React.Dispatch<React.SetStateAction<string>>;
  pineconeIndex: string;
  setPineconeIndex: React.Dispatch<React.SetStateAction<string>>;
  pineconeEnv: string;
  setPineconeEnv: React.Dispatch<React.SetStateAction<string>>;
  llmHasPineconeKey: boolean;
  
  saveLLMConfig: () => Promise<void>;
  
  // Profile Mutations
  editingName: string;
  setEditingName: React.Dispatch<React.SetStateAction<string>>;
  savingAutofill: boolean;
  handleSaveProfile: () => Promise<void>;
  handleCreateProfile: (name: string, copyFromId?: string) => Promise<void>;
  handleDeleteProfile: () => Promise<void>;
  handleSetDefault: (id: string) => Promise<void>;
  handleFileUpload: (file: File, type: 'resume' | 'cv' | 'template') => Promise<void>;
  
  // Auth & Extended Config
  user: User;
  editingResumeGenConfig: AutofillProfileDoc['resumeGenConfig'];
  setEditingResumeGenConfig: React.Dispatch<React.SetStateAction<AutofillProfileDoc['resumeGenConfig']>>;
}

const DEFAULT_SETTINGS: UserSettings = {
  notifications: true,
  theme: 'system',
  dailyApplicationGoal: 5,
  autoArchiveDays: 90,
  typingSpeed: 50,
};

const DEFAULT_AUTOFILL: AutofillProfile = {
  firstName: '', lastName: '', email: '', phone: '',
  address: '', city: '', state: '', zipCode: '', country: '',
  currentJobTitle: '', summary: '', linkedinUrl: '', portfolioUrl: '', githubUrl: '',
  workAuthorization: '', requiresSponsorship: false,
  salaryExpectation: '', yearsOfExperience: '',
  gender: '', hispanicLatino: '', race: '', veteranStatus: '', disabilityStatus: '',
};

const ProfileSettingsContext = createContext<ProfileSettingsContextType | undefined>(undefined);

export const ProfileSettingsProvider: React.FC<{ user: User; children: React.ReactNode }> = ({ user, children }) => {
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS);
  const [profiles, setProfiles] = useState<AutofillProfileDoc[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [editingDetails, setEditingDetails] = useState<AutofillProfile>(DEFAULT_AUTOFILL);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { setTheme } = useTheme();

  // LLM State
  const [llmTextProvider, setLlmTextProvider] = useState<LLMTextProvider>('gemini');
  const [llmTextApiKey, setLlmTextApiKey] = useState('');
  const [llmCustomTextUrl, setLlmCustomTextUrl] = useState('');
  const [llmEmbedProvider, setLlmEmbedProvider] = useState<LLMEmbedProvider | ''>('');
  const [llmEmbedApiKey, setLlmEmbedApiKey] = useState('');
  const [llmCustomEmbedUrl, setLlmCustomEmbedUrl] = useState('');
  const [llmHasTextKey, setLlmHasTextKey] = useState(false);
  const [llmHasEmbedKey, setLlmHasEmbedKey] = useState(false);

  // Pinecone state
  const [pineconeApiKey, setPineconeApiKey] = useState('');
  const [pineconeIndex, setPineconeIndex] = useState('');
  const [pineconeEnv, setPineconeEnv] = useState('');
  const [llmHasPineconeKey, setLlmHasPineconeKey] = useState(false);

  const [savingAutofill, setSavingAutofill] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [editingResumeGenConfig, setEditingResumeGenConfig] = useState<AutofillProfileDoc['resumeGenConfig']>(undefined);

  const loadProfiles = useCallback(async () => {
    const db = getFirestore();
    const profilesRef = collection(db, 'users', user.uid, 'autofillProfiles');
    const snapshot = await getDocs(profilesRef);
    const loadedProfiles: AutofillProfileDoc[] = [];
    snapshot.forEach(docSnap => {
      const data = docSnap.data();
      loadedProfiles.push({
        id: docSnap.id,
        name: data.name || 'Unnamed Profile',
        isDefault: data.isDefault || false,
        associatedResumeId: data.associatedResumeId,
        details: data.details || DEFAULT_AUTOFILL,
        resumeGenConfig: data.resumeGenConfig || undefined,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      });
    });
    loadedProfiles.sort((a, b) => (a.isDefault ? -1 : b.isDefault ? 1 : a.name.localeCompare(b.name)));
    setProfiles(loadedProfiles);
    return loadedProfiles;
  }, [user.uid]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'users', user.uid), {
        settings,
        updatedAt: new Date(),
      });
      setMessage({ type: 'success', text: 'Settings saved successfully' });
    } catch (err: any) {
      setMessage({ type: 'error', text: `Failed to save: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const saveLLMConfig = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await saveLLMConfigService({
        textProvider: llmTextProvider,
        textApiKey: llmTextApiKey || '',
        customTextUrl: llmCustomTextUrl || undefined,
        embeddingProvider: llmEmbedProvider || undefined,
        embeddingApiKey: llmEmbedApiKey || undefined,
        customEmbeddingUrl: llmCustomEmbedUrl || undefined,
        pineconeApiKey: pineconeApiKey || undefined,
        pineconeIndex: pineconeIndex || undefined,
        pineconeEnv: pineconeEnv || undefined,
      });

      if (llmTextApiKey) setLlmHasTextKey(true);
      if (llmEmbedApiKey) setLlmHasEmbedKey(true);
      if (pineconeApiKey) setLlmHasPineconeKey(true);
      
      setLlmTextApiKey('');
      setLlmEmbedApiKey('');
      setPineconeApiKey('');
      
      setMessage({ type: 'success', text: 'AI configuration saved and encrypted successfully.' });
    } catch (error: any) {
      console.error('Error saving LLM config:', error);
      setMessage({ type: 'error', text: `Failed to save AI config: ${error.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!selectedProfileId) return;
    setSavingAutofill(true);
    setMessage(null);
    try {
      const db = getFirestore();
      const profileRef = doc(db, 'users', user.uid, 'autofillProfiles', selectedProfileId);
      
      // Get existing config to support partial updates
      await getDoc(profileRef);

      await updateDoc(profileRef, {
        name: editingName,
        details: editingDetails,
        resumeGenConfig: editingResumeGenConfig || null,
        updatedAt: new Date(),
      });

      setProfiles(prev => prev.map(p =>
        p.id === selectedProfileId ? { 
          ...p, 
          name: editingName, 
          details: editingDetails, 
          resumeGenConfig: editingResumeGenConfig,
          updatedAt: new Date() 
        } : p
      ));
      setMessage({ type: 'success', text: 'Profile saved successfully!' });
    } catch (err: any) {
      setMessage({ type: 'error', text: `Save failed: ${err.message}` });
    } finally {
      setSavingAutofill(false);
    }
  };

  const handleCreateProfile = async (name: string, copyFromId?: string) => {
    setSavingAutofill(true);
    try {
      const db = getFirestore();
      const profilesRef = collection(db, 'users', user.uid, 'autofillProfiles');
      let details = { ...DEFAULT_AUTOFILL, email: user.email || '' };
      if (copyFromId) {
        const source = profiles.find(p => p.id === copyFromId);
        if (source) details = { ...source.details };
      }
      const isFirst = profiles.length === 0;
      const docRef = await addDoc(profilesRef, {
        name, isDefault: isFirst, details, createdAt: new Date(), updatedAt: new Date()
      });
      await loadProfiles();
      setSelectedProfileId(docRef.id);
      setMessage({ type: 'success', text: `Profile "${name}" created!` });
    } catch (err: any) {
      setMessage({ type: 'error', text: `Create failed: ${err.message}` });
    } finally {
      setSavingAutofill(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!selectedProfileId || profiles.length <= 1) return;
    setSavingAutofill(true);
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'users', user.uid, 'autofillProfiles', selectedProfileId));
      const remaining = await loadProfiles();
      setSelectedProfileId(remaining[0]?.id || null);
      setMessage({ type: 'success', text: 'Profile deleted.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: `Delete failed: ${err.message}` });
    } finally {
      setSavingAutofill(false);
    }
  };

  const handleSetDefault = async (profileId: string) => {
    try {
      const db = getFirestore();
      const batch = writeBatch(db);
      profiles.forEach(p => {
        batch.update(doc(db, 'users', user.uid, 'autofillProfiles', p.id), { isDefault: p.id === profileId });
      });
      await batch.commit();
      await loadProfiles();
      setMessage({ type: 'success', text: 'Default profile updated.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: `Update failed: ${err.message}` });
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.settings) {
          setSettings(prev => ({ ...prev, ...data.settings }));
          if (data.settings.theme) setTheme(data.settings.theme);
        }
        if (data.llmConfig) {
          const cfg = data.llmConfig;
          setLlmTextProvider(cfg.textProvider || 'gemini');
          setLlmHasTextKey(cfg.hasTextKey || false);
          setLlmCustomTextUrl(cfg.customTextUrl || '');
          setLlmEmbedProvider(cfg.embeddingProvider || '');
          setLlmHasEmbedKey(cfg.hasEmbeddingKey || false);
          setLlmCustomEmbedUrl(cfg.customEmbeddingUrl || '');
          setLlmHasPineconeKey(cfg.hasPineconeKey || false);
          setPineconeIndex(cfg.pineconeIndex || '');
          setPineconeEnv(cfg.pineconeEnv || '');
        }
      }
      await loadProfiles();
      setLoading(false);
    };
    init();
  }, [user.uid, loadProfiles, setTheme]);

  useEffect(() => {
    const profile = profiles.find(p => p.id === selectedProfileId);
    if (profile) {
      setEditingName(profile.name);
      setEditingDetails(profile.details);
      setEditingResumeGenConfig(profile.resumeGenConfig);
    }
  }, [selectedProfileId, profiles]);

  const handleFileUpload = async (file: File, type: 'resume' | 'cv' | 'template') => {
    if (!user || !selectedProfileId) return;
    setUploadingFile(true);
    setMessage(null);
    try {
      let name = '';
      if (type === 'resume') name = 'Master Resume';
      else if (type === 'cv') name = 'Full CV';
      else name = 'Typst Template';

      const savedResume = await createSavedResume(user.uid, file, name);
      
      const db = getFirestore();
      const profileRef = doc(db, 'users', user.uid, 'autofillProfiles', selectedProfileId);
      
      const updatedDetails = { ...editingDetails };
      const docUpdates: any = {
        updatedAt: new Date()
      };

      if (type === 'resume') {
        updatedDetails.defaultResumePath = savedResume.storagePath;
        updatedDetails.defaultResumeUrl = savedResume.downloadUrl;
        updatedDetails.defaultResumeName = file.name;
        docUpdates.associatedResumeId = savedResume.id;
      } else if (type === 'cv') {
        updatedDetails.defaultCvPath = savedResume.storagePath;
        updatedDetails.defaultCvUrl = savedResume.downloadUrl;
        updatedDetails.defaultCvName = file.name;
      } else if (type === 'template') {
        updatedDetails.defaultTypstTemplatePath = savedResume.storagePath;
        updatedDetails.defaultTypstTemplateUrl = savedResume.downloadUrl;
        updatedDetails.defaultTypstTemplateName = file.name;
      }

      docUpdates.details = updatedDetails;
      await updateDoc(profileRef, docUpdates);
      
      // Update local state
      setEditingDetails(updatedDetails);
      if (type === 'resume') {
        setProfiles(prev => prev.map(p => p.id === selectedProfileId ? { ...p, associatedResumeId: savedResume.id, details: updatedDetails } : p));
      } else {
        setProfiles(prev => prev.map(p => p.id === selectedProfileId ? { ...p, details: updatedDetails } : p));
      }

      setMessage({ type: 'success', text: `${name} uploaded successfully!` });
    } catch (error: any) {
      console.error('Upload failed:', error);
      setMessage({ type: 'error', text: `Upload failed: ${error.message}` });
    } finally {
      setUploadingFile(false);
    }
  };

  return (
    <ProfileSettingsContext.Provider value={{
      settings, setSettings, profiles, setProfiles,
      selectedProfileId, setSelectedProfileId,
      editingDetails, setEditingDetails,
      loading, saving, setSaving, message, setMessage,
      loadProfiles, saveSettings,
      
      llmTextProvider, setLlmTextProvider,
      llmTextApiKey, setLlmTextApiKey,
      llmCustomTextUrl, setLlmCustomTextUrl,
      llmEmbedProvider, setLlmEmbedProvider,
      llmEmbedApiKey, setLlmEmbedApiKey,
      llmCustomEmbedUrl, setLlmCustomEmbedUrl,
      llmHasTextKey, llmHasEmbedKey,
      
      pineconeApiKey, setPineconeApiKey,
      pineconeIndex, setPineconeIndex,
      pineconeEnv, setPineconeEnv,
      llmHasPineconeKey,
      
      saveLLMConfig,
      
      editingName, setEditingName,
      savingAutofill,
      handleSaveProfile,
      handleCreateProfile,
      handleDeleteProfile,
      handleSetDefault,
      handleFileUpload,
      uploadingFile,
      user,
      editingResumeGenConfig,
      setEditingResumeGenConfig
    }}>
      {children}
    </ProfileSettingsContext.Provider>
  );
};

export const useProfileSettings = () => {
  const context = useContext(ProfileSettingsContext);
  if (!context) throw new Error('useProfileSettings must be used within ProfileSettingsProvider');
  return context;
};
